export type PageAccessConfig = Record<string, string[]>;

export interface AppPage {
  key: string;
  name: string;
  href: string;
  sortOrder: number;
}

const STORAGE_KEY = 'restro360_role_page_config';
const APP_PAGES_KEY = 'restro360_app_pages';
const ACTIVE_RESTAURANT_KEY = 'restro360_active_restaurant';
const ACTIVE_ROLE_KEY = 'restro360_active_role';

export function getActiveRestaurantId(): string | null {
  return localStorage.getItem(ACTIVE_RESTAURANT_KEY);
}

export function setActiveRestaurantId(id: string | null): void {
  if (id) localStorage.setItem(ACTIVE_RESTAURANT_KEY, id);
  else localStorage.removeItem(ACTIVE_RESTAURANT_KEY);
}

export function getActiveRole(): string | null {
  return localStorage.getItem(ACTIVE_ROLE_KEY);
}

export function setActiveRole(role: string | null): void {
  if (role) localStorage.setItem(ACTIVE_ROLE_KEY, role);
  else localStorage.removeItem(ACTIVE_ROLE_KEY);
}

/** Read config from localStorage cache (populated after login from backend). */
export function getPageAccessConfig(): PageAccessConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as PageAccessConfig;
  } catch {
    // ignore
  }
  return {};
}

/** Cache config in localStorage (called after fetching from backend). */
export function savePageAccessConfig(config: PageAccessConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

/** The pages defined in Role Page Access (the single source of truth for the nav). */
export function getAppPages(): AppPage[] {
  try {
    const stored = localStorage.getItem(APP_PAGES_KEY);
    if (stored) return JSON.parse(stored) as AppPage[];
  } catch {
    // ignore
  }
  return [];
}

export function saveAppPages(pages: AppPage[]): void {
  localStorage.setItem(APP_PAGES_KEY, JSON.stringify(pages));
}

/** Resolve a route path to its page key by matching the page's href. */
export function pageKeyForPath(pathname: string): string | undefined {
  return getAppPages().find(p => p.href === pathname)?.key;
}

/** Decode JWT (no verification) and return the roles claim. */
export function getUserRolesFromToken(): string[] {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return [];
    const payload = JSON.parse(atob(token.split('.')[1]));
    const roles = payload.roles;
    if (Array.isArray(roles)) return roles as string[];
    if (typeof roles === 'string') return [roles];
    return [];
  } catch {
    return [];
  }
}

/** Global platform owner (the SaaS provider). Lives outside any organization. */
const PLATFORM_ADMIN_ROLES = ['SUPPER_ADMIN', 'SUPER_ADMIN', 'PLATFORM_ADMIN'];
export function isPlatformAdmin(roles: string[]): boolean {
  return Array.isArray(roles) && roles.some(r => PLATFORM_ADMIN_ROLES.includes((r || '').toUpperCase()));
}

/** A page is visible only if one of the user's roles is granted it in Role Page Access. */
export function canAccessPage(pageKey: string, userRoles: string[], config: PageAccessConfig): boolean {
  const allowed = config[pageKey];
  if (!allowed || allowed.length === 0) return false;
  return userRoles.some(r => allowed.includes(r));
}

/** First accessible page's href (by sortOrder) — a safe redirect that never loops. */
export function firstAccessiblePath(userRoles: string[], config: PageAccessConfig): string | null {
  const page = getAppPages()
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .find(p => canAccessPage(p.key, userRoles, config));
  return page ? page.href : null;
}
